import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { Check, ChevronDown, Copy, Mail, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface TipSchoolProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

const NEWLINE = String.fromCharCode(10)
const GROEP_OPTIES = ['4', '5', '6', '7', '8']
const INPUT_CLASS = 'block w-full rounded-xl border border-input bg-white px-4 py-3 text-base text-ink transition placeholder:text-muted-foreground/70 focus:border-leaf focus:ring-4 focus:ring-leaf/15 focus:outline-none'

const buildMessage = (school: string, groep: string, voornaam: string): string => {
    const aanhef = school ? `Beste leerkracht of directie van ${school},` : "Beste leerkracht of directie,"
    const vraag = groep ? `Misschien iets voor groep ${groep}?` : "Misschien iets voor onze school?"
    const afsluiting = voornaam ? `Hartelijke groet,${NEWLINE}${voornaam}` : "Hartelijke groet,"

    return [
        aanhef,
        '',
        "Ik kwam Leesritme tegen, een leesapp voor groep 1 tot en met 8. Kinderen lezen elke dag een rijke tekst van tien minuten in de klas, en thuis een tweede tekst over hetzelfde onderwerp. Het is een aanvulling op de leesmethode en de app kijkt zelf na.",
        '',
        `Scholen kunnen het eerst gratis proberen met een pilot, zonder verplichting. ${vraag} Meer informatie staat op leesritme.nl.`,
        '',
        afsluiting
    ].join(NEWLINE)
}

export const TipSchool: FC<TipSchoolProps> = ({ open, onOpenChange }): ReactNode => {
    const [school, setSchool] = useState('')
    const [groep, setGroep] = useState('')
    const [voornaam, setVoornaam] = useState('')
    const [notify, setNotify] = useState(false)
    const [notified, setNotified] = useState('')
    const [copied, setCopied] = useState(false)

    const message = buildMessage(school.trim(), groep, voornaam.trim())
    const mailHref = `mailto:?subject=${encodeURIComponent("Leesritme: iets voor onze school?")}&body=${encodeURIComponent(message)}`
    const whatsappHref = `https://wa.me/?text=${encodeURIComponent(message)}`

    const reportTip = (): void => {
        const name = school.trim()

        if (!notify || !name || notified === name) return

        setNotified(name)

        fetch('/api/aanmelden', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'tip', school: name, groep, naam: voornaam.trim() })
        }).catch(() => undefined)
    }

    const copyMessage = async (): Promise<void> => {
        reportTip()

        try {
            await navigator.clipboard.writeText(message)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2200)
        }

        catch {
            setCopied(false)
        }
    }

    return (
        <div className='rounded-[1.75rem] border border-coral/25 bg-coral-soft/50'>
            <button
                type='button'
                aria-expanded={open}
                aria-controls='tip-de-school'
                onClick={() => onOpenChange(!open)}
                className='flex w-full items-center justify-between gap-4 rounded-[1.75rem] p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf sm:p-6'
            >
                <span>
                    <span className='block font-heading text-lg leading-snug font-semibold text-ink'>
                        Liever dat de hele klas meedoet? Tip de school
                    </span>

                    <span className='mt-0.5 block text-sm text-muted-foreground'>
                        Voor ouders: wij schrijven het berichtje alvast voor je.
                    </span>
                </span>

                <span className='grid size-10 shrink-0 place-items-center rounded-full bg-white text-ink'>
                    <ChevronDown className={cn('size-5 transition-transform duration-200', open && 'rotate-180')} aria-hidden='true' />
                </span>
            </button>

            {open && (
                <div id='tip-de-school' className='border-t border-coral/20 p-5 sm:p-6'>
                    <p className='text-[0.9375rem] leading-relaxed text-muted-foreground'>
                        Stuur de leerkracht of directeur van je kind een berichtje. Mail en WhatsApp openen met het bericht al ingevuld: jij kiest zelf naar wie je het stuurt.
                    </p>

                    <div className='mt-5 grid gap-4 sm:grid-cols-2'>
                        <div className='sm:col-span-2'>
                            <label htmlFor='tip-school' className='text-sm font-bold text-ink'>
                                Naam van de school
                            </label>

                            <input
                                id='tip-school'
                                type='text'
                                value={school}
                                onChange={event => setSchool(event.target.value)}
                                autoComplete='organization'
                                maxLength={120}
                                className={cn(INPUT_CLASS, 'mt-1.5')}
                            />
                        </div>

                        <div>
                            <label htmlFor='tip-groep' className='flex items-baseline justify-between gap-2 text-sm font-bold text-ink'>
                                Groep van je kind
                                <span className='text-xs font-normal text-muted-foreground'>mag leeg</span>
                            </label>

                            <select
                                id='tip-groep'
                                value={groep}
                                onChange={event => setGroep(event.target.value)}
                                className={cn(INPUT_CLASS, 'mt-1.5')}
                            >
                                <option value=''>Maakt niet uit</option>
                                {GROEP_OPTIES.map(option => (
                                    <option key={option} value={option}>
                                        Groep {option}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor='tip-voornaam' className='flex items-baseline justify-between gap-2 text-sm font-bold text-ink'>
                                Je voornaam
                                <span className='text-xs font-normal text-muted-foreground'>mag leeg</span>
                            </label>

                            <input
                                id='tip-voornaam'
                                type='text'
                                value={voornaam}
                                onChange={event => setVoornaam(event.target.value)}
                                autoComplete='given-name'
                                maxLength={60}
                                className={cn(INPUT_CLASS, 'mt-1.5')}
                            />
                        </div>
                    </div>

                    <div className='mt-5 rounded-2xl bg-white p-4 shadow-soft'>
                        <p className='text-xs font-bold tracking-[0.12em] text-muted-foreground uppercase'>
                            Je bericht
                        </p>

                        <p className='mt-2 text-sm leading-relaxed whitespace-pre-line text-ink'>
                            {message}
                        </p>
                    </div>

                    <label className='mt-5 flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink'>
                        <input
                            type='checkbox'
                            checked={notify}
                            onChange={event => setNotify(event.target.checked)}
                            className='mt-0.5 size-5 shrink-0 cursor-pointer rounded accent-leaf'
                        />

                        <span>
                            Laat Leesritme weten welke school ik tip. Dan kunnen we de school goed helpen als ze reageren.
                            <span className='block text-muted-foreground'>We nemen zelf geen contact op met de school.</span>
                        </span>
                    </label>

                    <div className='mt-5 flex flex-wrap gap-2'>
                        <a
                            href={mailHref}
                            onClick={reportTip}
                            className='inline-flex min-h-11 items-center gap-2 rounded-full bg-ink px-5 text-[0.9375rem] font-semibold text-paper transition hover:bg-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2'
                        >
                            <Mail className='size-4' aria-hidden='true' />
                            Mail de school
                        </a>

                        <a
                            href={whatsappHref}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={reportTip}
                            className='inline-flex min-h-11 items-center gap-2 rounded-full bg-[#17784a] px-5 text-[0.9375rem] font-semibold text-white transition hover:bg-[#11603b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2'
                        >
                            <MessageCircle className='size-4' aria-hidden='true' />
                            Via WhatsApp
                        </a>

                        <button
                            type='button'
                            onClick={copyMessage}
                            className='inline-flex min-h-11 items-center gap-2 rounded-full border border-ink/15 bg-white px-5 text-[0.9375rem] font-semibold text-ink transition hover:border-ink/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-leaf focus-visible:ring-offset-2'
                        >
                            {copied ? <Check className='size-4 text-leaf' aria-hidden='true' /> : <Copy className='size-4' aria-hidden='true' />}
                            {copied ? 'Gekopieerd' : 'Kopieer tekst'}
                        </button>
                    </div>

                    <p className='sr-only' aria-live='polite'>
                        {copied ? 'De tekst is gekopieerd.' : ''}
                    </p>
                </div>
            )}
        </div>
    )
}
